import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { FileUploadModule, FileUploader } from 'ng2-file-upload';
import { AppService } from 'src/app/app.service';
import { AuthService } from 'src/app/auth.service';
import { EnvService } from 'src/app/env.service';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { ContentService } from 'src/app/views/content/content.service';
import { MySpaceService } from 'src/app/views/my-space/my-space.service';
import { environment } from 'src/environments/environment';
import { MaterialModule } from '../../material.module';
import { TenantService } from '../../services/tenant/tenant.service';
import { ToastService } from '../../services/toast/toast.service';
import { UploadService } from '../../services/upload/upload.service';
import { ContentSearcherComponent } from '../content-searcher/content-searcher.component';

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  standalone: true,
  imports: [TranslateModule, MaterialModule, FileUploadModule, FormsModule, TranslateModule],
})
export class FileUploaderComponent implements OnInit {
  linkPrefix = environment.linkPrefix;
  loading = false;
  url = this.env.apiUrl;
  sendAsRes = false;
  inProgress;
  linkBucketlessPrefix = 'https://storage.googleapis.com/';
  @Input() urlPath; // 'http://localhost:3200/file_upload/'
  @Input() params;
  @Input() type;
  @Input() origin;
  @Input() lecture;
  @Input() language;
  @Input() folder;
  @Input() useFor;

  @Output() success = new EventEmitter<any>();
  public uploader: FileUploader;
  public hasBaseDropZoneOver = false;
  selectedExtractorTabIndex = 0;
  public imageExtentionList = ['jpg', 'jpeg', 'jpe', 'jif', 'jfif', 'jfi', 'png', 'bmp'];
  public videoExtentionList = ['webm', 'mpg', 'mkv', 'mp4', 'wegm', 'ogg', 'avi', 'flv', 'wmv', 'm4v'];
  public audioExtensionList = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'wma', 'm4a', 'aiff', 'alac', 'opus'];
  currentLecture;
  imageResolution;
  incorrectResolutionError = false;
  imageGenerationPrompt = '';
  generatedImageURL;
  storage;
  selectedResolution = '1024x1024';
  selectedStyle = 'vivid';
  generatedImageResolutions = [
    {
      label: 'MySpace.AIImageResolutions.Square',
      value: '1024x1024',
    },
    {
      label: 'MySpace.AIImageResolutions.Portrait',
      value: '1024x1792',
    },
    {
      label: 'MySpace.AIImageResolutions.Landscape',
      value: '1792x1024',
    },
  ];

  generatedImageStyle = [
    {
      label: 'MySpace.AIImageStyles.Vivid',
      value: 'vivid',
    },
    {
      label: 'MySpace.AIImageStyles.Natural',
      value: 'natural',
    },
  ];
  currentLocation;
  constructor(
    private toast: ToastService,
    private appService: AppService,
    private contentService: ContentService,
    private router: Router,
    private authService: AuthService,
    private env: EnvService,
    private chunkService: ChunkService,
    private translate: TranslateService,
    private mySpaceService: MySpaceService,
    private dialog: MatDialog,
    private uploadService: UploadService,
    private tenantService: TenantService
  ) {
    this.uploader = new FileUploader({
      // TODO: Angular 16 update required to update the package. url is now required
      url: '',
    });
    this.storage = inject(Storage);

    this.uploader.onAfterAddingFile = (item) => {
      if (this.type == 'personalize-logo') {
        this.getImageResolution(item._file).then((res) => {
          this.imageResolution = res;
          if (this.params.size) {
            const size = this.params.size;
            if (this.imageResolution.width != size.x || this.imageResolution.height != size.y) {
              this.incorrectResolutionError = true;
            } else {
              this.incorrectResolutionError = false;
            }
          }
        });
      }
      if (this.origin == 'media-edition') {
        if (!item.file.type.includes(this.params.type)) {
          this.toast.snackbar.open(`${this.translate.instant('Snackbar.WrongFileFormatError')}${this.params.type}`, 'Ok', { duration: 2000 });
          this.uploader.clearQueue();
        }
      }
      this.uploader.queue = this.uploader.queue.slice(this.uploader.queue.length - 1);
    };
    this.appService.currentLecture$.subscribe((res) => (this.currentLecture = res));
    // this.uploader.onBeforeUploadItem =

    this.uploader.onSuccessItem = (item) => {
      this.toast.snackbar.open(this.translate.instant('Snackbar.FileUploadSuccess'), 'Close', {
        duration: 2000,
      });
      this.success.emit(true);
    };

    this.uploader.onErrorItem = (item, response) => {
      console.error('error', response);
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', {
        duration: 2000,
      });
    };
  }

  ngOnInit() {
    if (this.router.url.includes('myspace')) {
      this.currentLocation = 'myspace';
    }
  }

  setIsUploading(value: boolean) {
    if (this.uploader) {
      this.uploader.queue = this.uploader.queue.map((item) => {
        item.isUploading = value;
        return item;
      });
    }
  }

  setError(value: boolean) {
    if (this.uploader) {
      this.uploader.queue = this.uploader.queue.map((item) => {
        item.isError = value;
        return item;
      });
    }
  }

  setSuccess(value: boolean) {
    if (this.uploader) {
      this.uploader.queue = this.uploader.queue.map((item) => {
        item.isSuccess = value;
        return item;
      });
    }
  }

  setProgress(value: number) {
    if (this.uploader) {
      this.uploader.queue = this.uploader.queue.map((item) => {
        item.progress = value;

        return item;
      });
    }
  }

  getImageResolution(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const src = e.target?.result;
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
          reject('There was an error loading the image');
        };
        if (typeof src === 'string') {
          img.src = src;
        }
      };
      reader.readAsDataURL(file);
    });
  }

  uploadItem(item) {
    this.selectedExtractorTabIndex = 0;
    let filename = item._file.name;
    const extension = filename.substr(filename.lastIndexOf('.') + 1);
    this.inProgress = true;

    if (this.type == 'personalize-app') {
      this.authService.getUserData().then((userData) => {
        const userID = (userData as any).id;
        const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
        const imageFileName = `${activeTenant}_${userID}.jpg`;
        const allowedExtensions = ['png', 'jpg'];
        if (allowedExtensions.includes(extension)) {
          this.uploadService.uploadFileAndGetProgressWitoutAppFolderPrefix(item._file, this.folder + '/' + imageFileName).on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              this.setProgress(Math.floor(progress));
            },
            (error) => {
              console.error('upload fail', error);
              this.setError(true);
              this.inProgress = false;
              this.setIsUploading(false);
            },
            () => {
              const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
              const fileLinkRes = `${this.linkPrefix}${activeTenant + '/' + this.folder + '/' + imageFileName}`;
              this.success.emit(fileLinkRes);
              this.inProgress = false;
              this.setSuccess(true);
            }
          );
        } else {
          this.setError(true);
          this.inProgress = false;
          this.toast.snackbar.open(this.translate.instant('Snackbar.WrongFileFormatError') + allowedExtensions, 'Ok', { duration: 2000 });
        }
      });
    } else {
      // this.uploader.clearQueue()
      this.origin = this.origin || this.params.origin;
      this.setIsUploading(true);
      this.loading = true;
      let filenameSmall;

      let filenamePrefix;
      if (this.urlPath && this.urlPath.includes('profile_image')) {
        // for the profile picture the filename is the id of the user so they always match
        // it allows to not have to sync the pictures whenever there is a change
        filenamePrefix = this.params.id;
      } else {
        filenamePrefix = Math.random().toString(36).substring(7) + Math.random().toString(36).substring(7) + moment().unix();
      }

      filename = filenamePrefix + '.' + extension;
      filenameSmall = filenamePrefix + '_thumbnail' + '.' + extension;

      this.params.lecture = this.currentLecture;
      if (this.urlPath && this.urlPath.includes('profile_image')) {
        this.params.type = 'profile_image';
        this.params._id = this.authService.userInfo()._id;
        this.authService.uploadFile(item._file, filename, environment.profilePictureBucketName, false).then(() => {
          filename = filenamePrefix + '_200x200' + '.' + extension;
          this.params.fileLink = `${this.linkBucketlessPrefix}${`${environment.profilePictureBucketName}/` + filename}`;

          this.mySpaceService.getUserProfile(this.params._id).subscribe((user) => {
            const data = {
              firstName: user.firstName,
              lastName: user.lastName,
              photo: this.params.fileLink,
            };
            this.mySpaceService
              .updateUserProfile({
                user,
                formData: data,
              })
              .subscribe((res) => {
                this.success.emit(this.params.fileLink);
              });
          });
        });
      }

      if (this.type == 'chunk_audio') {
        this.params.type = 'chunk_audio';
        const type = 'chunk_audios';
        // this.params._id = this.authService.userInfo()._id
        this.uploadService.uploadFileAndGetProgress(item._file, type + '/' + filename).on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            this.setProgress(Math.floor(progress));
          },
          (error) => {
            this.setError(true);
            this.inProgress = false;
            this.setIsUploading(false);
          },
          () => {
            const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
            this.params.fileLink = `${this.linkPrefix}${activeTenant + '/' + type + '/' + filename}`;
            this.contentService.submitChunkUpload(this.params).subscribe(
              (res) => {
                this.success.emit(res);
                this.inProgress = false;
                this.setSuccess(true);
                this.setIsUploading(false);
                this.chunkService.passChunkEditEvent({
                  event: 'uploadItem',
                  chunkID: this.params._id,
                });
              },
              (err) => {
                this.inProgress = false;
                this.setError(true);
              }
            );
          }
        );
      } else if (this.origin == 'create-chunk' || this.origin == 'media-edition') {
        this.params.language = this.language;
        this.params.origin = this.origin;
        this.params.filename = this.uploader.queue[0].file.name;
        this.params.type = item.file.type.substring(0, 5);
        if (this.imageExtentionList.includes(extension)) {
          this.params.type = 'image';
        } else if (this.videoExtentionList.includes(extension)) {
          this.params.type = 'video';
        } else if (this.audioExtensionList.includes(extension)) {
          this.params.type = 'audio';
        } else {
          if (this.origin == 'media-edition') {
            this.toast.snackbar.open(`${this.translate.instant('Snackbar.WrongFileFormatError')} ${this.params.type}`, 'Ok', { duration: 2000 });
            return;
          }
          this.params.type = 'file';
        }
        this.uploadService.uploadFileAndGetProgress(item._file, this.params.type + '/' + filename).on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            this.setProgress(progress);
          },
          (error) => {
            console.error(error);
            this.inProgress = false;
            this.setError(true);
            this.setIsUploading(false);
          },
          () => {
            const activeTenant = this.tenantService.activeTenantId() || this.env.appName;
            this.params.fileLink = `${this.linkPrefix}${activeTenant + '/' + this.params.type + '/' + filename}`;

            this.contentService.submitChunkUpload(this.params).subscribe(
              (res) => {
                this.success.emit(res);
                this.setSuccess(true);
                this.inProgress = false;
                this.setIsUploading(false);
              },
              (err) => {
                console.error(err);
                this.inProgress = false;
                this.setError(true);
              }
            );
          }
        );
      } else {
        item.url = this.url + this.urlPath + JSON.stringify(this.params);
      }
    }
  }

  public fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  async fetchImageAsFile(url: string, filename: string) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error(response);
        throw new Error(`${response}`);
      }
      const imageBlob = await response.blob();
      const imageFile = new File([imageBlob], filename, {
        type: imageBlob.type,
      });
      this.uploader.addToQueue([imageFile]);
    } catch (error) {
      console.error('Download error:', error);
    }
  }
  getDownloadURL(filename): Promise<string> {
    const storageRef = ref(this.storage, filename);
    return getDownloadURL(storageRef);
  }

  generateImageFromContent() {
    this.dialog
      .open(ContentSearcherComponent, {
        height: '50%',
        width: '75%',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((res) => {
        this.loading = true;
        this.contentService.generateImageFromContent(res.contentID, this.selectedResolution, this.selectedStyle).subscribe({
          next: (res) => {
            this.onImageRecieved(res);
            this.loading = false;
          },
          error: (err) => {
            console.error(err);
            this.loading = false;
          },
        });
      });
  }
  onImageRecieved(res) {
    const mimetype = 'image/png';
    const filename = res.filename;
    this.loading = false;
    this.getDownloadURL(res.filename).then((url) => {
      this.generatedImageURL = url;
      this.contentService.downloadAsArrayBuffer(url).subscribe((arrayBuffer) => {
        const blob = new Blob([arrayBuffer], { type: mimetype });
        const file = new File([blob], filename, { type: mimetype });
        this.uploader.addToQueue([file]);
      });
    });
  }
  generateImage() {
    this.loading = true;
    const imageInfo = {
      returnAsFile: true,
      prompt: this.imageGenerationPrompt,
      size: this.selectedResolution,
      style: this.selectedStyle,
    };
    this.chunkService.generateImage(imageInfo).subscribe({
      next: async (res) => {
        this.onImageRecieved(res);
        this.loading = false;
      },
      error: (error) => {
        console.error(error);
        this.loading = false;
      },
    });
  }
}
